// El borrador local de un PAE (Rápido o Detallado) y lo que se dice de él.
// Puro: sin React ni navegador, lo usa App.jsx (autoguardado, "¿Continuar
// donde lo dejaste?", la confirmación al salir) y sus pruebas.
//
// Hasta el 4-oct-2026 solo el Rápido tenía borrador: recargar el Detallado en
// el paso 7 perdía todo, y "Continuar" siempre abría un Rápido. El borrador
// ahora guarda su `mode`. Uno viejo sin `mode` es del Rápido (era el único que
// existía), así que se lee como Rápido y jamás se migra.

export const MODOS_BORRADOR = Object.freeze(['rapido', 'detallado']);

export function modoDelBorrador(datos) {
  return datos?.mode === 'detallado' ? 'detallado' : 'rapido';
}

export function nombreDelBorrador(datos) {
  return modoDelBorrador(datos) === 'detallado' ? 'PAE Detallado' : 'PAE Rápido';
}

// "Hace 5 min", y pasada la hora "Hace 2 h": "Hace 1380 min" no se lee.
export function haceCuanto(timestamp, ahora = Date.now()) {
  const min = Math.max(0, Math.round((ahora - Number(timestamp || 0)) / 60000));
  if (min < 1) return 'Hace un momento';
  if (min < 60) return `Hace ${min} min`;
  return `Hace ${Math.floor(min / 60)} h`;
}

function tieneContenido(v) {
  if (v == null || v === false) return false;
  if (typeof v === 'string') return v.trim() !== '';
  if (typeof v === 'number') return true;
  if (v === true) return true;
  if (Array.isArray(v)) return v.some(tieneContenido);
  if (typeof v === 'object') return Object.values(v).some(tieneContenido);
  return false;
}

// Las secciones que la persona escribe en la valoración. La unidad elegida
// sola no es "lo escrito": salir sin haber anotado nada no pregunta.
const SECCIONES_DETALLADO = ['anamnesis', 'neuro', 'resp', 'hemo', 'digest', 'renal', 'tegum', 'disp', 'lab'];

export function detalladoTieneDatos(formData = {}) {
  return SECCIONES_DETALLADO.some((k) => tieneContenido(formData?.[k]));
}

// Lo que el Rápido escribe solo, sin que la persona haya anotado nada: el
// modelo (elegido o recordado del dispositivo) y la huella del plan. Abrir el
// Rápido y volver sin escribir no deja "PAE Rápido sin terminar" (revisión
// del paso 2 de la interfaz por experiencia, 2026-10-16: con Tu PAE, asomarse
// dejaba un borrador vacío como protagonista). El plan sí cuenta.
const RAPIDO_NO_ESCRITO = ['modelo', 'modeloElegido', 'modeloAntesDeLibre', '_planHuella'];

export function rapidoTieneDatos(formData = {}) {
  const { rapido, ...resto } = formData || {};
  const propio = rapido && typeof rapido === 'object'
    ? Object.fromEntries(Object.entries(rapido).filter(([k]) => !RAPIDO_NO_ESCRITO.includes(k)))
    : rapido;
  return tieneContenido(propio) || tieneContenido(resto);
}

// Un borrador que se puede retomar: el de su modo con algo escrito.
export function borradorTieneDatos(datos) {
  return modoDelBorrador(datos) === 'detallado'
    ? detalladoTieneDatos(datos?.formData)
    : rapidoTieneDatos(datos?.formData);
}

// Lo que se guarda en localStorage. El Detallado suma su selección de
// diagnósticos: sin ella, continuar en el plan o en el resumen dejaba el PAE
// sin diagnósticos.
export function borradorParaGuardar({ mode, step, formData, selected, esNuevoPAE, ahora = Date.now() }) {
  const base = { mode: modoDelBorrador({ mode }), step, formData, esNuevoPAE, timestamp: ahora };
  return mode === 'detallado' ? { ...base, selected: Array.isArray(selected) ? selected : [] } : base;
}

// El id de sesión del borrador del Detallado lleva su prefijo: así un id que
// quedó de un Rápido (o de un borrador de otro tipo) jamás se reusa y pisa el
// borrador que no es suyo.
export const PREFIJO_SESION_DETALLADO = 'det-';

export function esSesionDetallado(id) {
  return typeof id === 'string' && id.startsWith(PREFIJO_SESION_DETALLADO);
}
